import * as React from 'react';
import KonvaImage from '../KonvaImage';
import { QTYPES, Question } from '@nexxt/common/types';
import { UilTrashAlt, UilPlus } from '@iconscout/react-unicons';
import Modal, {
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalTitle
} from '../../../../common/modal';
import { UilImagePlus, UilSync } from '@iconscout/react-unicons';
import { getObjectDetect } from '../../../../../services/question.service';
import { alertMessage } from '../../../../../constants/alertMessage';
import MediaManager from '../../../../libraries/mediaLibrary/MediaManager';

interface ComputerVisionProps {
    imageURL: string;
    question: Question;
    setValue: (keyName: string, value: any) => void;
    handleOptionFields: (
        keyName: string,
        value: any,
        deletedOptionIDs?: number[]
    ) => void;
    addNewOption: () => void;
    clearOptions: () => void;
    clearComputerVision: () => void;
    toggleMediaLibrary?: () => void;
    enableComputerVision: boolean;
    toggleEnableComputerVision: () => void;
    isLoading: boolean;
    updateIsLoading: (value: boolean) => void;
    updateOptions: (optionedCV: any[]) => void;
    selectMedia?: (selectedMedia: any) => void;
    toastNotification?: any;
}
const ComputerVision: React.FC<ComputerVisionProps> = (
    props: ComputerVisionProps
) => {
    const [showModal, setShowModal] = React.useState(false);
    const [showMediaManager, setShowMediaManager] = React.useState(false);
    const [clearType, setClearType] = React.useState<string>(null);
    const [selectedMedia, setSelectedMedia] = React.useState(null);
    const [showLabels, setShowLabels] = React.useState(true);

    const {
        question,
        isLoading,
        updateIsLoading,
        imageURL,
        toggleMediaLibrary,
        enableComputerVision,
        toggleEnableComputerVision,
        updateOptions,
        toastNotification
    } = props;

    const toggleModal = (type: string) => {
        setClearType(type);
        setShowModal((prevState) => !prevState);
    };

    React.useEffect(() => {
        if (enableComputerVision && imageURL && question.options.length < 1) {
            getComputerVision();
        }
    }, [imageURL]);

    const getComputerVision = async () => {
        updateIsLoading(true);
        const response = await getObjectDetect(imageURL);
        if (response.code === 200) {
            const optionedCV = response.boxes.map((o, i) => {
                return {
                    label: o.text,
                    value: String(i + 1),
                    coordinates: {
                        // @TODO: use `ImageAnnotationOptionCoordinates` type from dashboard
                        rotation: 0,
                        x: o.x,
                        y: o.y,
                        height: o.height,
                        width: o.width
                    }
                };
            });

            updateOptions(optionedCV);
        } else {
            toastNotification.error(alertMessage.COMPUTER_VISION_FAIL);
        }
        updateIsLoading(false);
    };

    const onMediaConfirm = (medias) => {
        let activeMedia;
        if (medias?.length) {
            activeMedia = medias[0].mediaURL;
        } else if (selectedMedia) {
            activeMedia = selectedMedia;
        }

        props.selectMedia({ src: activeMedia });
    };

    return (
        <div className="computer-vision--wrapper">
            {!isLoading && imageURL ? (
                <div className="computer-vision--container">
                    {showModal && (
                        <Modal className="modal__heightauto" isOpen={showModal}>
                            <ModalHeader onClose={() => toggleModal(null)}>
                                <ModalTitle>Confirmation</ModalTitle>
                            </ModalHeader>
                            <ModalBody>
                                {clearType === 'image'
                                    ? 'Are you sure you would like to remove this image?'
                                    : 'Are you sure you would like to remove all areas?'}
                            </ModalBody>
                            <ModalFooter>
                                <button
                                    className="btn btn--sm btn--transparent m-r-10"
                                    onClick={() => toggleModal(null)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn--sm btn--orange"
                                    onClick={() => {
                                        toggleModal(null);
                                        if (clearType === 'image') {
                                            props.clearComputerVision();
                                        } else {
                                            props.clearOptions();
                                        }
                                    }}
                                >
                                    Remove
                                </button>
                            </ModalFooter>
                        </Modal>
                    )}
                    <div className="computer-vision--header">
                        <span
                            className="computer-vision--header--button --add"
                            onClick={props.addNewOption}
                        >
                            <UilPlus size="14" />
                            Add New Area
                        </span>
                        <div className="setup-wizard--checkbox">
                            <input
                                id="computer-vision-checkbox"
                                type="checkbox"
                                defaultChecked={showLabels}
                                onChange={() =>
                                    setShowLabels((prevState) => !prevState)
                                }
                                disabled={isLoading}
                            />
                            <label htmlFor="computer-vision-checkbox">
                                Show Labels
                            </label>
                        </div>
                        <span
                            className="computer-vision--header--button"
                            onClick={() => toggleModal('areas')}
                        >
                            Clear All Areas
                        </span>
                        {props.question?.type === QTYPES.HOTSPOT && (
                            <span
                                className="computer-vision--header--button --clear"
                                onClick={() => toggleModal('image')}
                            >
                                <UilTrashAlt size="14" /> Remove Image
                            </span>
                        )}
                    </div>
                    <div className="computer-vision--body">
                        <KonvaImage
                            imageURL={props.imageURL}
                            question={props.question}
                            handleOptionFields={props.handleOptionFields}
                            showLabels={showLabels}
                        />
                    </div>
                </div>
            ) : (
                <div className="computer-vision--container">
                    <div className="computer-vision--header">
                        <MediaManager
                            showMediaLibrary
                            showMediaManager={showMediaManager}
                            toggleMediaManager={setShowMediaManager}
                            type="image"
                            applyCurrentMedia={({ mediaURL }) =>
                                setSelectedMedia(mediaURL)
                            }
                            onConfirm={onMediaConfirm}
                        >
                            <button
                                className="computer-vision--header--button --add"
                                onClick={() => {
                                    setShowMediaManager(true);
                                    toggleMediaLibrary();
                                }}
                            >
                                Add Image
                            </button>
                        </MediaManager>
                        <div className="setup-wizard--checkbox">
                            <input
                                id="computer-vision-checkbox"
                                type="checkbox"
                                defaultChecked={enableComputerVision}
                                onChange={toggleEnableComputerVision}
                                disabled={isLoading}
                            />
                            <label htmlFor="computer-vision-checkbox">
                                Enable AI generated hotspots
                            </label>
                        </div>
                    </div>
                    <div
                        className="computer-vision--body"
                        style={{
                            cursor: isLoading ? 'default' : 'pointer'
                        }}
                        onClick={(e) => {
                            if (!isLoading) {
                                e.stopPropagation();
                                setShowMediaManager(true);
                                toggleMediaLibrary();
                            }
                        }}
                    >
                        {isLoading ? (
                            <div className="setup-wizard--body--file --no-border --darker">
                                <UilSync size={60} />

                                <span className="setup-wizard--body--file--text">
                                    Please wait as we generate your hotspots
                                </span>
                            </div>
                        ) : (
                            <div className="setup-wizard--body--file --no-border --darker">
                                <UilImagePlus size={60} />

                                <span className="setup-wizard--body--file--text">
                                    Upload image from a file
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ComputerVision;
